import { useMemo, useState } from 'react';
import { Check, History, RotateCcw, Wand2 } from 'lucide-react';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useKbRevision, useKbRevisions, useMarkKbReviewed, useRevertKbArticle } from '@/queries/kb';
import type { KbArticle, KbRevision, KbReviewReason } from '@/types/kb';
import { diffLines, type DiffLine } from './kbDiff';

export const REVIEW_REASON_LABELS: Record<KbReviewReason, string> = {
  created: 'New guide',
  rewritten: 'Rewritten',
  removed: 'Journey removed'
};

const REVIEW_HEADINGS: Record<KbReviewReason, string> = {
  created: 'A new generated guide is waiting for review',
  rewritten: 'This guide was rewritten when its journey ran. Check what changed.',
  removed: 'This guide was unpublished because its journey no longer exists'
};

export function revisionAuthor(revision: KbRevision): string {
  if (revision.authorKind === 'generator') return 'Generated from its journey';
  if (revision.authorKind === 'agent') return 'Agents';
  if (revision.authorKind === 'unknown') return 'Earlier version';
  return revision.authorId?.name || revision.authorId?.email || 'Staff';
}

// What a reader sees, so a change to the title or subtitle shows in the diff as well as one to the body.
const readable = (a: { title?: string; subtitle?: string; content?: string }) =>
  `# ${a.title ?? ''}\n${a.subtitle ?? ''}\n\n${a.content ?? ''}`;

// Unchanged runs longer than this are folded, so the change is what the reviewer sees.
const CONTEXT = 3;

function foldUnchanged(lines: DiffLine[]): Array<DiffLine | { kind: 'fold'; count: number }> {
  const out: Array<DiffLine | { kind: 'fold'; count: number }> = [];
  let run: DiffLine[] = [];
  const flush = (atEnd: boolean) => {
    const keepStart = out.length ? CONTEXT : 0;
    const keepEnd = atEnd ? 0 : CONTEXT;
    if (run.length > keepStart + keepEnd + 1) {
      out.push(...run.slice(0, keepStart), { kind: 'fold', count: run.length - keepStart - keepEnd }, ...run.slice(run.length - keepEnd));
    } else {
      out.push(...run);
    }
    run = [];
  };
  lines.forEach(line => {
    if (line.kind === 'same') run.push(line);
    else {
      flush(false);
      out.push(line);
    }
  });
  flush(true);
  return out;
}

function ContentDiff({ before, after }: { before: string; after: string }) {
  const lines = useMemo(() => foldUnchanged(diffLines(before, after)), [before, after]);
  if (before === after) return <p className="text-sm text-muted-foreground">The text did not change.</p>;
  return (
    <pre className="max-h-96 overflow-auto rounded border bg-background p-3 text-xs leading-relaxed" data-testid="kb-review-diff">
      {lines.map((line, i) =>
        line.kind === 'fold' ? (
          <div key={i} className="text-muted-foreground">
            … {line.count} unchanged line{line.count === 1 ? '' : 's'}
          </div>
        ) : (
          <div
            key={i}
            className={
              line.kind === 'added'
                ? 'bg-etn-success-subtle text-etn-success-text'
                : line.kind === 'removed'
                  ? 'bg-etn-danger-subtle text-etn-danger-text line-through'
                  : undefined
            }
          >
            <span aria-hidden className="select-none pr-2">
              {line.kind === 'added' ? '+' : line.kind === 'removed' ? '-' : ' '}
            </span>
            <span className="sr-only">{line.kind === 'added' ? 'Added: ' : line.kind === 'removed' ? 'Removed: ' : ''}</span>
            {line.text || ' '}
          </div>
        )
      )}
    </pre>
  );
}

/** The review and history panel above the editor: what a generated change did, and every earlier version. */
export function KbReviewPanel({ article }: { article: KbArticle }) {
  const revisionsQuery = useKbRevisions(article._id);
  const revisions = revisionsQuery.data?.data;
  const totalRevisions = revisionsQuery.data?.total ?? 0;
  // The version this change replaced, as the API recorded it; not simply the second-newest, since staff may have saved
  // since, and a change to tags or flags alone replaces no text.
  const previousId = article.review?.reason === 'rewritten' ? article.review.previousRevisionId : undefined;
  const previousText = useKbRevision(article._id, previousId);
  const markReviewed = useMarkKbReviewed(article._id);
  const revert = useRevertKbArticle(article._id);
  const [showHistory, setShowHistory] = useState(false);
  const needsReview = article.review?.status === 'needs_review';

  async function restore(revisionId: string, label: string) {
    const warning = article.journey ? ' The next time its journey runs, the guide will be rewritten again.' : '';
    if (!confirm(`Restore ${label}?${warning}`)) return;
    try {
      await revert.mutateAsync(revisionId);
      toast.success('Earlier version restored');
    } catch (err) {
      toast.error((err as Error).message ?? 'Could not restore it');
    }
  }

  if (!article.journey && !needsReview && totalRevisions < 2) return null;

  return (
    <section className="container mx-auto max-w-4xl px-6 pt-6 space-y-3" aria-label="Review and history">
      {article.journey && (
        <div
          className="flex items-start gap-2 rounded border border-etn-info-subtle bg-etn-info-subtle p-3 text-sm text-etn-info-text"
          data-testid="kb-journey-banner"
        >
          <Wand2 aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Generated from the <strong>{article.journey.id}</strong> journey. Edits made here are replaced the next time the journey runs.
          </p>
        </div>
      )}

      {needsReview && article.review?.reason && (
        <div className="space-y-3 rounded border border-etn-warning-subtle p-4" data-testid="kb-review-panel">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-medium">{REVIEW_HEADINGS[article.review.reason]}</h2>
            <div className="flex gap-2">
              {article.review.reason === 'rewritten' && previousId && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={revert.isPending}
                  onClick={() => restore(previousId, 'the version before this rewrite')}
                >
                  <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                  Revert to previous
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                disabled={markReviewed.isPending}
                onClick={async () => {
                  try {
                    await markReviewed.mutateAsync();
                    toast.success('Marked as reviewed');
                  } catch (err) {
                    toast.error((err as Error).message ?? 'Could not mark it reviewed');
                  }
                }}
              >
                <Check className="mr-1.5 h-3.5 w-3.5" />
                Mark reviewed
              </Button>
            </div>
          </div>
          {article.review.reason === 'created' && (
            <p className="text-sm text-muted-foreground">
              It is saved as a draft. Read it below, choose its collection, and publish it when it is right.
            </p>
          )}
          {article.review.reason === 'rewritten' &&
            (previousId ? (
              previousText.data ? (
                <ContentDiff before={readable(previousText.data)} after={readable(article)} />
              ) : (
                <p className="text-sm text-muted-foreground">Loading the previous version…</p>
              )
            ) : (
              <p className="text-sm text-muted-foreground">The text did not change: only its tags or feature flags did.</p>
            ))}
        </div>
      )}

      {totalRevisions > 1 && (
        <div>
          <Button type="button" size="sm" variant="ghost" onClick={() => setShowHistory(v => !v)} aria-expanded={showHistory}>
            <History className="mr-1.5 h-3.5 w-3.5" />
            {showHistory ? 'Hide history' : `History (${totalRevisions} versions)`}
          </Button>
          {showHistory && (
            <ul className="mt-2 divide-y rounded border bg-background text-sm" data-testid="kb-history">
              {revisions?.map((revision, i) => (
                <li key={revision._id} className="flex items-center gap-3 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate">{revision.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {revisionAuthor(revision)}, {dayjs(revision.createdAt).format('D MMM YYYY, HH:mm')}
                    </div>
                  </div>
                  {i === 0 ? (
                    <Badge variant="neutral">Current</Badge>
                  ) : (
                    <Button type="button" size="sm" variant="ghost" disabled={revert.isPending} onClick={() => restore(revision._id, 'this version')}>
                      Restore
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {showHistory && revisions && totalRevisions > revisions.length && (
            <p className="mt-1 text-xs text-muted-foreground">
              Showing the latest {revisions.length} of {totalRevisions} versions.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
